import UnsavedChanges from '@/components/unsavedChanges/UnsavedChanges';
import { mapLegacyAddressToNewFormat } from '@/utils/address';
import { isRichTextAreaEmpty } from '@/utils/richText';
import { Value } from '@udecode/plate-common';
import { FC, PropsWithChildren, useCallback, useMemo, useState } from 'react';
import { create } from 'zustand';
import { Contact } from '../api/commonTypes';
import { PurchaseOrderDeliveryOption } from '../api/contracts/catalog/bundles';
import { FinanceModel, PeripheralFinanceModel } from '../api/contracts/financemodels';
import { useCreateAddress, useUpdateAddress } from '../api/hooks/address';
import { useCreatePurchaseOrderBundle, useUpdatePurchaseOrderBundle } from '../api/hooks/bundles';
import { useCreateContactDetail, useUpdateContact } from '../api/hooks/contact';
import { useFinanceModels, usePeripheralFinanceModels } from '../api/hooks/financemodels';
import { PODeliveryAddress, SelectedProduct } from './purchaseOrder.types';
import { PoContact, PoDelivery, PoStatus, PurchaseOrderContext, PurchaseOrderInfo } from './usePurchaseOrderService';
import { usePurchaseOrder } from './usePurchaseOrders';
import { usePurchaseOrderNavigationBlocker } from './validation/usePurchaseOrderNavigationBlocker';

interface Props {
  customerId: string;
  partnerId: string;
  orderId?: string;
}

export const PurchaseOrderProvider: FC<PropsWithChildren<Props>> = ({ customerId, children, partnerId, orderId }) => {
  const storedOrder = usePurchaseOrder(orderId);

  const saveAddress = useCreateAddress();
  const updateAddress = useUpdateAddress();

  const saveContact = useCreateContactDetail();
  const updateContact = useUpdateContact();

  const createPurchaseOrder = useCreatePurchaseOrderBundle();
  const updatePurchaseOrder = useUpdatePurchaseOrderBundle();

  const [registerUnsavedChanges, setRegisterUnsavedChanges] = useState(false);

  const determineUnsavedChanges = useCallback(
    (products: SelectedProduct[]) => {
      const storedProducts = storedOrder?.bundledItems ?? [];

      const storedMap = new Map(storedProducts.map(item => [item.catalogItem, item.count]));
      const localMap = new Map(products.map(item => [item.productId, item.quantity]));

      for (const { catalogItem, count } of storedProducts) {
        if (localMap.has(catalogItem)) {
          if (localMap.get(catalogItem) !== count) {
            // Count has changed
            setRegisterUnsavedChanges(true);
            return;
          }
        } else {
          setRegisterUnsavedChanges(true);
          // Item removed in the local copy
          return;
        }
      }
      for (const { productId } of products) {
        if (!storedMap.has(productId)) {
          // New item added locally
          setRegisterUnsavedChanges(true);
          return;
        }
      }

      setRegisterUnsavedChanges(false);
    },
    [storedOrder]
  );

  const blocker = usePurchaseOrderNavigationBlocker(registerUnsavedChanges);

  const models = useFinanceModels();
  const peripheralModels = usePeripheralFinanceModels();

  const store = useMemo(
    () =>
      create<PurchaseOrderInfo>((set, get) => ({
        selectedProducts: storedOrder
          ? storedOrder.bundledItems.map(item => {
              return {
                quantity: item.count,
                productId: item.catalogItem
              };
            })
          : [],
        orderName: storedOrder?.name,
        selectProduct: (productId: string) => {
          const newList = [...get().selectedProducts];

          if (newList.length === 0) {
            // Ideally this should be a hook, but calling selectFinanceModel & selectPeripheralFinanceModel
            // via the existing hooks in usePurchaseService seems to use the empty functions
            const defaultModels = models.filter(m => m.isDefault);
            if (defaultModels) {
              get().selectFinanceModel(defaultModels);
            }

            const defaultPeripheralModel = peripheralModels.find(m => m.isDefault);
            if (defaultPeripheralModel) {
              get().selectPeripheralFinanceModel(defaultPeripheralModel);
            }
          }

          const indexInList = newList.findIndex((p: SelectedProduct) => p.productId === productId);
          if (indexInList >= 0) {
            newList.splice(indexInList, 1);
          } else {
            newList.push({ productId, quantity: 1 });
          }

          set({ selectedProducts: newList });

          determineUnsavedChanges(newList);
        },
        setProductQuantity: (productId: string, newQuantity: number) => {
          const newList = [...get().selectedProducts];

          const indexInList = newList.findIndex(p => p.productId === productId);

          if (indexInList === -1) {
            throw Error('Could not find product in selected list');
          }
          newList.splice(indexInList, 1, { productId, quantity: newQuantity });

          set({ selectedProducts: newList });
          determineUnsavedChanges(newList);
        },
        selectedFinanceModels: storedOrder?.financeModels ?? [],
        selectedPeripheralFinanceModel: storedOrder?.peripheralFinanceModel,
        selectCapitalPurchaseForMainDevice: (categoryId: string) => {
          const selectedFinanceModels = [...get().selectedFinanceModels];

          const financeModelsToKeep = selectedFinanceModels.filter(m => m.categoryId !== categoryId);

          set({ selectedFinanceModels: financeModelsToKeep });
        },
        selectCapitalPurchaseForPeripheral: () => {
          set({ selectedPeripheralFinanceModel: undefined });
        },
        selectFinanceModel: (incomingModels: FinanceModel[]) => {
          let selectedFinanceModels = [...get().selectedFinanceModels];

          const financeModelsForCategory = selectedFinanceModels.filter(
            m => m.categoryId === incomingModels[0]?.categoryId
          );

          if (financeModelsForCategory.length > 0) {
            selectedFinanceModels = selectedFinanceModels.filter(
              selectedModel =>
                !incomingModels.some(incomingModel => incomingModel.categoryId === selectedModel.categoryId)
            );
          }

          selectedFinanceModels.push(...incomingModels);

          set({ selectedFinanceModels: selectedFinanceModels });
        },
        selectPeripheralFinanceModel: (model: PeripheralFinanceModel) => {
          set({ selectedPeripheralFinanceModel: model });
        },
        savePurchaseOrder: async (nameToSave: string, status: PoStatus) => {
          const financeModelIds = get().selectedFinanceModels.map(model => model.id);

          const notes = get().notes;
          const isNotesEmpty = !notes || isRichTextAreaEmpty(notes.value);

          const payload = {
            customerId,
            mspId: partnerId,
            addressId: get().delivery?.addressId,
            contactDetailId: get().contact?.contactId,
            deliveryOption: get().delivery?.type ?? PurchaseOrderDeliveryOption.Daas,
            name: nameToSave,
            bundledItems: get().selectedProducts.map(item => {
              return {
                catalogItem: item.productId,
                count: item.quantity
              };
            }),
            poReference: get().poRef,
            stockingOrderNumber: get().stockingOrder,
            partnerReference: get().partnerRef,
            financeModelIds,
            peripheralFinanceModelId: get().selectedPeripheralFinanceModel?.id,
            state: status,
            deviceFeeCatalogItemId: get().selectedDeviceFeeId,
            ...(!isNotesEmpty && { notes })
          };

          if (orderId) {
            await updatePurchaseOrder.mutateAsync({ ...payload, id: orderId! });
          } else {
            await createPurchaseOrder.mutateAsync(payload);
          }
        },
        storeDeliveryDetails: async (
          type: PurchaseOrderDeliveryOption,
          address?: PODeliveryAddress,
          updateBackend?: boolean
        ) => {
          let addressId = get().delivery?.addressId;

          if (type === PurchaseOrderDeliveryOption.Custom && updateBackend) {
            if (!address) throw new Error('Missing custom address');

            const { name, ...rest } = address;

            const payload = {
              name,
              saveToAddressBook: false,
              ...mapLegacyAddressToNewFormat(rest)
            };

            if (addressId) {
              await updateAddress.mutateAsync({ ...payload, id: addressId, mspId: partnerId, customerId });
            } else {
              const addressResponse = await saveAddress.mutateAsync({ ...payload, mspId: partnerId, customerId });

              addressId = addressResponse.id;
            }
          }

          set({
            delivery: {
              type,
              addressId
            } as PoDelivery
          });
        },
        clearDeliveryAddressId: () => {
          set(state => ({
            delivery: state.delivery ? { ...state.delivery, addressId: undefined } : undefined
          }));
        },
        delivery: {
          type: storedOrder?.deliveryOption ?? 0,
          addressId: storedOrder?.address?.id
        },
        contact: {
          type: storedOrder?.deliveryOption ?? 0,
          contactId: storedOrder?.contactDetail?.id
        },
        storeContactDetails: async (type: PurchaseOrderDeliveryOption, contact?: Contact, updateBackend?: boolean) => {
          let contactId = get().contact?.contactId;

          if (type === PurchaseOrderDeliveryOption.Custom && updateBackend) {
            if (!contact) throw new Error('Missing contact');

            const payload = {
              workEmail: contact.workEmail,
              telephoneNumber: contact.telephoneNumber,
              personalEmail: contact.personalEmail
            };

            if (contactId) {
              await updateContact.mutateAsync({ ...payload, id: contactId });
            } else {
              const contactResponse = await saveContact.mutateAsync(payload);

              contactId = contactResponse.id;
            }
          }

          set({
            contact: {
              type,
              contactId
            } as PoContact
          });
        },
        status: storedOrder?.state || PoStatus.Draft,
        setStatus: (status: PoStatus) => {
          set({
            status
          });
        },
        partnerRef: storedOrder?.partnerReference,
        poRef: storedOrder?.poReference,
        stockingOrder: storedOrder?.stockingOrderNumber,
        notes: storedOrder?.notes,
        setPartnerRef: (val?: string) => {
          set({ partnerRef: val });
        },
        setPoRef: (val?: string) => {
          set({ poRef: val });
        },
        setStockingOrder: (val?: string) => {
          set({ stockingOrder: val });
        },
        setNotes: (val?: { value: Value }) => {
          set({ notes: val });
        },
        selectedDeviceFeeId: storedOrder?.deviceFeeCatalogItem?.id,
        selectDeviceFee: (id: string) => {
          set({ selectedDeviceFeeId: id });
        },
        submittedOnDate: storedOrder?.submittedOn ? new Date(storedOrder?.submittedOn) : undefined
      })),
    [customerId, partnerId, orderId]
  );

  return (
    <>
      <UnsavedChanges show={blocker} />
      <PurchaseOrderContext.Provider value={store}>{children}</PurchaseOrderContext.Provider>
    </>
  );
};
